import React, { ReactElement, Dispatch, useState } from 'react';
import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import styles from 'components/pages/playersCompare/list/base/CompareTable.module.scss';
import H1 from 'components/typography/H1';
import Text from 'components/typography/Text';
import Button from 'components/forms/Button';
import Modal from 'components/modal/Modal';
import { useTranslation } from 'utils/localization';
import Autocomplete from 'components/forms/Autocomplete';
import { getMemberList } from 'services/member.service';
import { MemberPlayerListApiInterface } from 'submodules/api_middleware';
import { Option } from 'components/interfaces/GeneralInterface';
import { PersonService } from 'services/person.service';
import {
    MessageType,
    pushMessage,
    redirectWithMessage,
} from 'components/layouts/Toast';
import { getRoute } from 'constants/routes';

interface Props {
    openModal: boolean;
    setOpenModal: Dispatch<boolean>;
}

const MergeModal = ({ openModal, setOpenModal }: Props): ReactElement => {
    const { t } = useTranslation();

    const [sourceID, setSourceID] = useState<string>();
    const [targetID, setTargetID] = useState<string>();

    const toggleModal = () => {
        setOpenModal(!openModal);
    };

    const searchPersons = async (
        query?: NextApiRequestQuery | string
    ): Promise<Option[]> => {
        const response = await getMemberList(query as NextApiRequestQuery);

        return response.data.data.map(
            (person: MemberPlayerListApiInterface) => ({
                label: `${person.person.name} (${person.member.internal_id})`,
                value: person.person.id,
            })
        );
    };

    const handleMerge = async () => {
        const response = await new PersonService().mergePersons(
            Number(sourceID),
            Number(targetID)
        );

        if (response.error) {
            return pushMessage(
                t('Members.mergeError'),
                MessageType.DANGER,
                undefined,
                true
            );
        }

        return redirectWithMessage(getRoute('/members/{0}', [targetID]), {
            messageType: MessageType.SUCCESS,
            message: t('Compare.mergeSuccessful'),
        });
    };

    return (
        <Modal
            show={openModal}
            onHide={toggleModal}
            loadingText={t('Members.mergingPersons')}
            successText={t('Compare.mergeSuccessful')}
            hideAfterSuccess
            isAlertModal
        >
            <div className={styles.modalRoot}>
                <H1>{t('Members.personMergeTitle') as string}</H1>
                <div className={styles.modalContent}>
                    <div className={styles.modalContentPart}>
                        <Text weight="medium">{t('Compare.leftLabel')}</Text>
                        <div className={styles.input}>
                            <Autocomplete
                                name="source_id"
                                options={undefined}
                                onSearch={searchPersons}
                                initValuesCb={searchPersons}
                                onChange={({
                                    target,
                                }: React.ChangeEvent<HTMLSelectElement>) =>
                                    setSourceID(target.value)
                                }
                                value={sourceID}
                                bigger
                                withBorder
                            />
                        </div>
                    </div>
                    <div className={styles.modalContentDivider}>
                        <span className={styles.arrowIcon} />
                    </div>
                    <div className={styles.modalContentPart}>
                        <Text weight="medium">{t('Compare.rightLabel')}</Text>
                        <div className={styles.input}>
                            <Autocomplete
                                name="target_id"
                                options={undefined}
                                onSearch={searchPersons}
                                initValuesCb={searchPersons}
                                onChange={({
                                    target,
                                }: React.ChangeEvent<HTMLSelectElement>) =>
                                    setTargetID(target.value)
                                }
                                value={targetID}
                                bigger
                                withBorder
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.buttonWrap}>
                    <Button
                        onClick={handleMerge}
                        isDisabled={!targetID || !sourceID}
                    >
                        {t('general.merge').toUpperCase()}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default MergeModal;
